
import api from './api';
import { LoginRequest, LoginResponse, User, RegisterRequest } from '../types';

// Helper function to check if it's a network error
const isNetworkError = (error: any): boolean => {
  return !error.response && (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK'));
};

export const authApi = {

  // Login user
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const response = await api.post('/auth/login', credentials);
      const loginData = response.data;

      // Store token and user info
      localStorage.setItem('token', loginData.access_token);
      localStorage.setItem('user_id', loginData.user_id);
      localStorage.setItem('username', loginData.username);
      localStorage.setItem('role', loginData.role);

      // Set token expiration
      const expirationTime = Date.now() + (loginData.expires_in * 1000);
      localStorage.setItem('token_expiration', expirationTime.toString());

      return loginData;
    } catch (error: any) {
      if (isNetworkError(error)) {
        console.error('Network error - API server may be down:', error.message);
        throw new Error('Unable to connect to server. Please check your internet connection or try again later.');
      }

      // Handle specific error responses
      if (error.response?.status === 401) {
        throw new Error('Invalid username or password');
      }
      if (error.response?.status === 422) {
        throw new Error('Invalid input data');
      }

      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  },

  // Register user
  register: async (userData: RegisterRequest): Promise<LoginResponse> => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error: any) {
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection or try again later.');
      }

      // Handle specific error responses
      if (error.response?.status === 422) {
        const detail = error.response.data?.detail;
        if (Array.isArray(detail)) {
          throw new Error(detail.map((err: any) => err.msg).join(', '));
        }
        throw new Error('Invalid input data');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.detail || 'Username already exists');
      }

      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  },

  // Get current user from localStorage
  getCurrentUser: (): User | null => {
    const userId = localStorage.getItem('user_id');
    const username = localStorage.getItem('username');
    const role = localStorage.getItem('role');

    if (!userId || !username || !role) {
      return null;
    }

    return {
      id: userId,
      username,
      role,
      is_active: true,
      created_at: new Date().toISOString(),
    };
  },

  // Check if token is expired
  isTokenExpired: (): boolean => {
    const expiration = localStorage.getItem('token_expiration');
    if (!expiration) return true;

    return Date.now() > parseInt(expiration);
  },

  // Clear authentication data
  clearAuthData: (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('token_expiration');
  },

  // Logout user
  logout: async (): Promise<void> => {
    try {
      // Note: New API doesn't have logout endpoint, just clear local data
      authApi.clearAuthData();
    } catch (error: any) {
      console.warn('Logout process failed, but clearing local storage');
      authApi.clearAuthData();
    }
  },

  // Change Password
  changePassword: async (oldPassword: string, newPassword: string): Promise<void> => {
    try {
      await api.post('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword
      });
    } catch (error: any) {
      if (isNetworkError(error)) {
        throw new Error('Network error. connectivity issue.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to change password');
    }
  },
};
